import { type ReactNode, useContext } from "react";
import type { RowData } from "@tanstack/react-table";
import { useIdCopy } from "@hooks/useIdCopy";
import type { DataGridLabelSlotOptions } from "../columns/kindOptions";
import { SummaryRowContext } from "./summaryRow";
import type { DataGridCellProps } from "./types";
import styles from "./cellContent.module.scss";

interface CopyableLabelProps {
    id: string;
    copyLabel: string | undefined;
    children: ReactNode;
}

/** The label with `BreakableText`'s right-click copy ID. */
const CopyableLabel = ({
    id,
    copyLabel,
    children,
}: Readonly<CopyableLabelProps>) => {
    const { getCopyProps } = useIdCopy({ label: copyLabel });
    return (
        <span className={styles.slotLabel} {...getCopyProps(id)}>
            {children}
        </span>
    );
};

interface LabelSlotsProps<
    TData extends RowData,
> extends DataGridCellProps<TData> {
    slots: DataGridLabelSlotOptions<TData>;
    label: ReactNode;
}

/**
 * `leading`, the label, then `trailing` in one row, outside the label's
 * editor trigger. A `toggleSlot` leading moves to `TreeCell`'s chevron slot
 * on rows that can't expand, and summary rows drop it.
 */
export const LabelSlots = <TData extends RowData>({
    context,
    meta,
    slots: { leading, leadingPlacement, trailing: Trailing, copyId, copyLabel },
    label,
}: Readonly<LabelSlotsProps<TData>>) => {
    const isSummaryRow = useContext(SummaryRowContext);
    const isLeadingPlacedInTree =
        leadingPlacement === "toggleSlot" &&
        meta.tree &&
        (isSummaryRow || !context.row.getCanExpand());
    const Leading = isLeadingPlacedInTree ? undefined : leading;
    const row = context.row.original;
    const id = copyId?.(row);
    const rowCopyLabel =
        typeof copyLabel === "function" ? copyLabel(row) : copyLabel;
    return (
        <span className={styles.labelSlots}>
            {Leading ? <Leading context={context} meta={meta} /> : null}
            {id === undefined ? (
                <span className={styles.slotLabel}>{label}</span>
            ) : (
                <CopyableLabel id={id} copyLabel={rowCopyLabel}>
                    {label}
                </CopyableLabel>
            )}
            {Trailing ? <Trailing context={context} meta={meta} /> : null}
        </span>
    );
};
